import React, { useCallback, useMemo, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Grid,
    MenuItem,
    TextField,
} from '@mui/material';

import withProps from '@dyl/utils/withProps';
import Conditional from '@dyl/components/Conditional';
import LoadingScreen from '@dyl/components/LoadingScreen';

import useLogic from './logic';
import useStyles from './styles';
import OutputTaskList from './OutputTaskList';
import TaskPicker from './TaskPicker';
import Skills from '@dyl/constants/Skills';
import ScreenSaveCancelBlock from '@dyl/blocks/ScreenSaveCancelBlock';
import withLevel from "@dyl/utils/withLevel";
import ActivityLogs from '@dyl/components/ActivityLogs';
import SectionDividers from '@dyl/components/SectionDividers';

/** @type {{ value: string, label: string }[]} */
const SkillOptions = [
    { value: '', label: 'Chose a skill' },
    ...Skills,
];

export default withLevel(ComponentEditor);

/**
 * @param {Object} props
 * @param {boolean} props.isNew
 * @param {string} props.componentId
 * @param {string} props.level
 * @param {string} props.skill
 * @param {string} props.qpvReference
 */
function ComponentEditor(props) {
    const {
        isLoading,

        levels,
        level,
        onLevelChange,
        allowLevelChange,

        skill,
        onSkillChange,
        allowSkillChange,

        name,
        onNameChange,

        allowContentEditing,
        allowAddContent,
        taskOptions,

        selectedTasks,
        selectedTaskIds,
        setSelectedTaskIds,
        removeTask,

        allowSaving,
        saveComponent,
        isSaving,

        exportComponentKeys,

        qpvReference,
        onQpvReferenceChange,

        disableQpvRef,

    } = useLogic(props);

    const { globalLevel, isNew, componentId = undefined } = props;
    const classes = useStyles(props);

    const Dropdown = useMemo(() => withProps(TextField, {
        // className: classes.textField,
        variant: 'filled',
        select: true,
        fullWidth: true
    }), []);

    const [isAddDialogOpen, setAddDialogOpen] = useState(false);
    const openAddDialog = useCallback(() => setAddDialogOpen(true), []);

    return (
        <Box className={classes.root}>
            {/* Dropdown row */}
            <Grid container spacing={3}>
                {/* Choose a level */}
                <Grid item xs={2}>
                    <Dropdown
                        id="component-level"
                        label="Level"
                        value={level}
                        onChange={onLevelChange}
                        helperText={'e.g. Pre-A1'}
                        disabled={!allowLevelChange}
                    >
                        {levels.map(({ level_id, label }) => (
                            <MenuItem key={level_id} value={level_id}>
                                {label}
                            </MenuItem>
                        ))}
                    </Dropdown>
                </Grid>

                {/* Choose a skill */}
                <Grid item xs={2}>
                    <Dropdown
                        id="task-skill"
                        label="Skill"
                        value={skill}
                        onChange={onSkillChange}
                        disabled={!allowSkillChange}
                    >
                        {SkillOptions.map(({ value, label }) => (
                            <MenuItem key={value} value={value}>
                                {label}
                            </MenuItem>
                        ))}
                    </Dropdown>
                </Grid>
                <Grid item xs={2}>
                    <TextField
                        id="qpv-reference"
                        label="QPV (SAP component reference)"
                        variant="filled"
                        fullWidth
                        value={qpvReference}
                        onChange={onQpvReferenceChange}
                        InputLabelProps={{
                            shrink: Boolean(qpvReference),
                            'data-shrink': Boolean(qpvReference),
                        }}
                        disabled={disableQpvRef}
                    />
                </Grid>

                <Grid item xs={4} />
                <Grid item xs={4} justifyContent={'end'}>
                </Grid>
            </Grid>

            {/* Name & add button */}
            {/* NB: Visually, name is called 'ID' */}
            <Grid container spacing={3}>
                {/* Name */}
                <Grid item xs={6}>
                    <TextField
                        id="component-id"
                        label="Component ID"
                        variant="filled"
                        fullWidth
                        helperText={'Unique ID to the component'}
                        onChange={onNameChange}
                        value={name}
                    />
                </Grid>

                {allowContentEditing && (
                    // 'Add tasks' button
                    <Grid item xs={2} style={{ display: 'flex' }}>
                        <Button
                            color='primary'
                            onClick={openAddDialog}
                            disabled={!allowAddContent}
                        >
                            Add Tasks
                            {!allowAddContent && <CircularProgress color='primary' size={24} />}
                        </Button>
                    </Grid>
                )}
                <Grid item xs={2} />
                <Grid item xs={2} style={{ display: 'flex' }}>
                    <Button color='primary' onClick={() => exportComponentKeys(selectedTasks)}>Export Keys</Button>
                </Grid>
            </Grid>

            {/* Selected items */}
            <Conditional if={allowContentEditing}>
                <OutputTaskList
                    className={classes.taskList}
                    rows={selectedTasks}
                    removeItem={removeTask}
                    editLink={(task) => `/${globalLevel.label}/tasks/${task.task_type_id}/${task.task_id}`}
                />
            </Conditional>

            {/* Task selector dialog */}
            <TaskPicker
                open={isAddDialogOpen}
                onClose={() => setAddDialogOpen(false)}
                taskIds={selectedTaskIds}
                onConfirmChanges={setSelectedTaskIds}
                taskOptions={taskOptions}
            />

            {componentId && !isNew && <SectionDividers DividerProps={{ className: classes.divider }}>
                <ActivityLogs
                    logId={componentId}
                    logType='component'
                    reload={isSaving} />
            </SectionDividers>}
            <ScreenSaveCancelBlock
                disabled={isSaving}
                save={saveComponent}
                saveDisabled={!allowSaving}
                cancelTo={`/${globalLevel.label}/components`}
            />


            <LoadingScreen open={isLoading || isSaving} />
        </Box>
    );
}